import {
  getWindowDisplayId,
  getWindowRoleSync,
} from "@/desktop/use-window-role";
import { useActiveDisplay } from "@/desktop/use-active-display";
import {
  getOutboundScreensForContext,
  registerOutboundCaptureContexts,
  type OutboundCaptureContext,
} from "@/desktop/outbound-screenshot-metadata";
import {
  isOperatingStepKind,
  operatingStepsMayBePresented,
} from "@/lib/agent-presentation-batch";
import { frameGlobalRect } from "@/lib/frame-screen-rect";
import { resolveAgentPoint, resolveAgentRect } from "@/lib/pointing-transform";
import { useCanvasStore } from "@/stores/canvas-store";
import type {
  AgentPresentation,
  AgentPresentationStep,
  CapturePath,
  CaptureRect,
  FrameTarget,
  GlobalPath,
  GlobalRect,
  PresentationExclusionRegion,
  ResolvedPresentationPoint,
  ResolvedPresentationRect,
} from "@/lib/agent-presentation-types";
import { parsePresentationSteps } from "@/lib/agent-presentation-step-parser";
import { PROMPT_SCREENSHOT_CAPTURE_ID } from "@/lib/agent-presentation-screenshot-targets";
export type {
  AgentPresentation,
  AgentPresentationStep,
  PresentationLifecycle,
  ResolvedPresentationPoint,
  ResolvedPresentationRect,
} from "@/lib/agent-presentation-types";

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function string(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function number(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : undefined;
}

function captureContexts(value: unknown): OutboundCaptureContext[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is OutboundCaptureContext => {
    const context = record(item);
    const screen = record(context?.screen);
    return Boolean(
      string(context?.capture_id) &&
      string(context?.execution_id) &&
      screen &&
      number(screen.index) !== undefined &&
      number(screen.displayId) !== undefined,
    );
  });
}

function basePresentation(
  data: Record<string, unknown>,
  steps: AgentPresentationStep[],
): AgentPresentation {
  return {
    presentationId: string(data.presentation_id)!,
    playbackAttempt: number(data.playback_attempt) ?? 1,
    resumeReceipt: Array.isArray(record(data.resume_receipt)?.steps)
      ? (record(data.resume_receipt)!
          .steps as AgentPresentation["resumeReceipt"])
      : undefined,
    mode: data.mode === "operate" ? "operate" : "guide",
    executionId: string(data.execution_id),
    ownerId: string(data.owner_id),
    runtimeSessionId: string(data.runtime_session_id),
    cliSessionId: string(data.cli_session_id),
    projectId: string(data.project_id),
    enforceMonotonicEpoch: true,
    turnEpoch: number(data.turn_epoch) ?? 0,
    canvasSpaceId: string(data.canvas_space_id),
    spaceId: string(data.space_id),
    agentId: string(data.agent_id),
    agentName: string(data.name),
    agentColor: string(data.color),
    defaultFrameDisplayId:
      useActiveDisplay.getState().activeDisplayId ?? undefined,
    requestedAcknowledgement:
      data.requested_acknowledgement === "rendered" ||
      data.requested_acknowledgement === "completed"
        ? data.requested_acknowledgement
        : "accepted",
    steps,
  };
}

export function normalizeAgentPresentation(
  type: string,
  data: Record<string, unknown>,
): AgentPresentation | null {
  if (type === "canvas.presentation_sequence") {
    const steps = parsePresentationSteps(data.steps);
    if (
      data.protocol_version !== 1 ||
      !string(data.presentation_id) ||
      !string(data.execution_id) ||
      !string(data.project_id) ||
      !steps
    ) {
      return null;
    }
    const operatingIndexes = steps.flatMap((step, index) =>
      isOperatingStepKind(step.kind) || step.kind === "type" ? [index] : [],
    );
    const mode = data.mode === "operate" ? "operate" : "guide";
    const hasUserAction = steps.some(
      (step) => "user_action" in step && step.user_action,
    );
    if (
      hasUserAction &&
      (mode !== "guide" || data.requested_acknowledgement !== "completed")
    )
      return null;
    if (!operatingStepsMayBePresented(mode, operatingIndexes, steps.length)) {
      return null;
    }
    const contexts = captureContexts(data.capture_contexts);
    registerOutboundCaptureContexts(contexts);
    const executionId = string(data.execution_id);
    const ownedContext = contexts.find(
      (context) => context.execution_id === executionId,
    );
    const presentation = basePresentation(
      {
        ...data,
        cli_session_id: data.cli_session_id ?? ownedContext?.cli_session_id,
      },
      steps,
    );
    // Native capture ownership uses the MCP authorization session. The outer
    // event may carry a different provider CLI session used for lifecycle.
    presentation.captureSessionId = ownedContext?.cli_session_id ?? undefined;
    return presentation;
  }
  return null;
}

function contextFor(presentation: AgentPresentation, captureId?: string) {
  const isPromptScreenshot = captureId === PROMPT_SCREENSHOT_CAPTURE_ID;
  return getOutboundScreensForContext({
    captureId: isPromptScreenshot ? undefined : captureId,
    executionId: presentation.executionId,
    sessionId: presentation.runtimeSessionId,
    cliSessionId: presentation.captureSessionId ?? presentation.cliSessionId,
    requireBoundContext: isPromptScreenshot,
    // Visual guidance can arrive after a slow model turn. Keep its exact
    // screenshot grid as long as explicit captures; native input retains
    // the stricter default freshness limit.
    boundContextMaxAgeMs:
      presentation.mode === "guide" ? 10 * 60 * 1000 : undefined,
  });
}

function presentationDisplayId(explicit?: number): number | null {
  if (explicit !== undefined) return explicit;
  // Unbound geometry uses the owner display, matching native delivery's
  // owner-only default. Live cursor movement must not retarget a sequence.
  return getWindowRoleSync() === "owner" ? getWindowDisplayId() : null;
}

/** A frame's rect in global pixels: its focus or floating window, else its
 *  wall card (tile, tick or index fallback) or canvas node. */
function frameRect(
  target: FrameTarget,
  targetDisplayId?: number,
): ResolvedPresentationRect | null {
  const resolvedDisplayId = presentationDisplayId(targetDisplayId);
  if (resolvedDisplayId === null) return null;
  const rect = frameGlobalRect(target.frame_id, resolvedDisplayId);
  return rect
    ? {
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height,
        displayId: resolvedDisplayId,
      }
    : null;
}

export function resolvePresentationPoint(
  step: Extract<
    AgentPresentationStep,
    { kind: "point" | "click" | "hover" | "scroll" | "paste" | "key" }
  >,
  presentation: AgentPresentation,
): ResolvedPresentationPoint | null {
  if (step.global_point) {
    const displayId = presentationDisplayId(
      step.global_point.display_id ??
        step.global_point.screen_id ??
        step.target_display_id,
    );
    if (displayId === null) return null;
    return {
      x: step.global_point.x,
      y: step.global_point.y,
      displayId,
    };
  }
  // A key is only ever drawn where Denker put it: the element or window it was
  // aimed at, resolved at delivery into `global_point`.
  if (step.kind === "key") return null;
  if (step.capture_point) {
    const point = step.capture_point;
    const legacyScreen = number(
      (point as unknown as Record<string, unknown>).screen,
    );
    const resolved = resolveAgentPoint(
      {
        screen:
          point.capture_id === PROMPT_SCREENSHOT_CAPTURE_ID
            ? (legacyScreen ?? 1)
            : 1,
        x: point.x,
        y: point.y,
      },
      contextFor(presentation, point.capture_id),
    );
    return resolved
      ? {
          x: resolved.globalX,
          y: resolved.globalY,
          displayId: resolved.displayId,
        }
      : null;
  }
  const rect = step.frame_target
    ? frameRect(step.frame_target, step.target_display_id)
    : null;
  if (!rect) return null;
  return {
    x: rect.x + rect.width * (step.frame_target?.x ?? 0.5),
    y: rect.y + rect.height * (step.frame_target?.y ?? 0.5),
    displayId: rect.displayId,
  };
}

export function resolvePresentationRect(
  step: Extract<AgentPresentationStep, { kind: "highlight" | "draw" }>,
  presentation: AgentPresentation,
): ResolvedPresentationRect | null {
  return resolveRectTarget(
    presentation,
    step.global_rect,
    step.capture_rect,
    step.frame_target,
    step.target_display_id,
  );
}

function resolvePathTarget(
  presentation: AgentPresentation,
  globalPath?: GlobalPath,
  capturePath?: CapturePath,
  targetDisplayId?: number,
): ResolvedPresentationPoint[] | null {
  if (globalPath) {
    const displayId = presentationDisplayId(
      globalPath.display_id ?? globalPath.screen_id ?? targetDisplayId,
    );
    if (displayId === null) return null;
    return globalPath.points.map((point) => ({ ...point, displayId }));
  }
  if (!capturePath) return null;
  const screens = contextFor(presentation, capturePath.capture_id);
  const resolved = capturePath.points.map((point) =>
    resolveAgentPoint(
      {
        screen:
          capturePath.capture_id === PROMPT_SCREENSHOT_CAPTURE_ID
            ? (capturePath.screen_index ?? 1)
            : 1,
        ...point,
      },
      screens,
    ),
  );
  if (resolved.some((point) => !point)) return null;
  return resolved.map((point) => ({
    x: point!.globalX,
    y: point!.globalY,
    displayId: point!.displayId,
  }));
}

export function resolvePresentationDragPath(
  step: Extract<AgentPresentationStep, { kind: "drag" }>,
  presentation: AgentPresentation,
): ResolvedPresentationPoint[] | null {
  return resolvePathTarget(
    presentation,
    step.global_path,
    step.capture_path,
    step.target_display_id,
  );
}

function resolveRectTarget(
  presentation: AgentPresentation,
  globalRect?: GlobalRect,
  captureRect?: CaptureRect,
  targetFrame?: FrameTarget,
  targetDisplayId?: number,
): ResolvedPresentationRect | null {
  if (globalRect) {
    const displayId = presentationDisplayId(
      globalRect.display_id ?? globalRect.screen_id ?? targetDisplayId,
    );
    if (displayId === null) return null;
    return {
      x: globalRect.x,
      y: globalRect.y,
      width: globalRect.width,
      height: globalRect.height,
      displayId,
    };
  }
  if (captureRect) {
    const rect = captureRect;
    const legacyScreen = number(
      (rect as unknown as Record<string, unknown>).screen,
    );
    const resolved = resolveAgentRect(
      {
        screen:
          rect.capture_id === PROMPT_SCREENSHOT_CAPTURE_ID
            ? (legacyScreen ?? 1)
            : 1,
        x: rect.x,
        y: rect.y,
        width: rect.width,
        height: rect.height,
      },
      contextFor(presentation, rect.capture_id),
    );
    return resolved
      ? {
          x: resolved.globalX,
          y: resolved.globalY,
          width: resolved.width,
          height: resolved.height,
          displayId: resolved.displayId,
        }
      : null;
  }
  return targetFrame ? frameRect(targetFrame, targetDisplayId) : null;
}

export function resolvePresentationExclusions(
  regions: PresentationExclusionRegion[] | undefined,
  presentation: AgentPresentation,
  targetDisplayId?: number,
): ResolvedPresentationRect[] {
  return (regions ?? []).flatMap((region) => {
    const resolved =
      region.coordinate_space === "global_desktop"
        ? resolveRectTarget(
            presentation,
            region.rect,
            undefined,
            undefined,
            targetDisplayId,
          )
        : region.coordinate_space === "capture"
          ? resolveRectTarget(
              presentation,
              undefined,
              { ...region.rect, capture_id: region.capture_id },
              undefined,
              targetDisplayId,
            )
          : resolveRectTarget(
              presentation,
              undefined,
              undefined,
              { frame_id: region.frame_id },
              targetDisplayId,
            );
    return resolved ? [resolved] : [];
  });
}

export function resolvePresentationFramePoint(
  step: Extract<AgentPresentationStep, { kind: "frame" }>,
  presentation: AgentPresentation,
): ResolvedPresentationPoint | null {
  if (!step.global_point && !step.capture_point) return null;
  return resolvePresentationPoint(
    {
      kind: "point",
      step_id: step.step_id,
      target_display_id: step.target_display_id,
      global_point: step.global_point,
      capture_point: step.capture_point,
    },
    presentation,
  );
}

export function resolvePresentationFrameDisplayId(
  step: Extract<AgentPresentationStep, { kind: "frame" }>,
  presentation: AgentPresentation,
): number | null {
  if (!step.capture_point && step.target_display_id != null)
    return step.target_display_id;
  const cursorPoint = resolvePresentationFramePoint(step, presentation);
  if (cursorPoint?.displayId != null) return cursorPoint.displayId;
  if (step.target_display_id != null) return step.target_display_id;
  const userDisplayId =
    presentation.defaultFrameDisplayId ??
    useActiveDisplay.getState().activeDisplayId;
  if (userDisplayId != null) return userDisplayId;
  const agentCursor = presentation.agentId
    ? useCanvasStore.getState().agentCursors.get(presentation.agentId)
    : undefined;
  if (agentCursor?.displayId != null) return agentCursor.displayId;
  return getWindowRoleSync() === "owner" ? getWindowDisplayId() : null;
}
